import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import { Box, Typography } from '@glplus/glpweb-dsl';
import { useTranslation } from 'react-i18next';
import type { RunnerNavigationCounts } from '../lib/useRunnerNavigationCounts';
import type { NavLink } from './MainHeader';
import { badgeLabel } from '../lib/inbox';
import { runnerTheme as theme } from '../styles/runnerTheme';

export type NavigationCount = { count: number | null; attention?: boolean };
export function sectionCount(children: NavigationCount[]): NavigationCount {
  return {
    count: children.length === 0 || children.some(c => c.count === null) ? null : children.reduce((sum, c) => sum + (c.count ?? 0), 0),
    attention: children.some(c => c.attention && (c.count ?? 0) > 0),
  };
}

function Count({ count, attention }: NavigationCount) {
  if (count === null || count <= 0) return null;
  return <Box component="span" sx={{ flexShrink: 0, bgcolor: attention ? theme.orange : theme.navy, color: theme.white, minWidth: 22, height: 22, px: '6px', borderRadius: '11px', fontSize: '12px', fontWeight: 600, display: 'inline-flex', justifyContent: 'center', alignItems: 'center' }}>{badgeLabel(count)}</Box>;
}

type Row = NavLink & { icon?: string; external?: boolean; badge?: NavigationCount };
const publicSite = (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ?? 'https://www.grocerylistplus.com';
const rowSx = {
  display: 'flex', alignItems: 'center', gap: '12px', minHeight: 44,
  width: '100%', px: '16px', pl: '18px', py: '8px', fontFamily: 'var(--font-sans)',
  fontSize: '14px', lineHeight: 1.45, color: '#343434', textDecoration: 'none',
  '&:hover': { bgcolor: theme.group },
};

/** Groups start expanded and the panel scrolls independently of page content. */
export function SideNavigation({ nav, showInbox, unreadTotal, counts = {}, onNavigate }: {
  nav: NavLink[]; showInbox: boolean; unreadTotal: number; counts?: RunnerNavigationCounts; onNavigate?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const es = i18n.language.startsWith('es');
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);
  const isApplicant = !showInbox;
  const links = (paths: string[]) => paths.flatMap(path => nav.filter(n => n.to === path));
  const groups = isApplicant ? [
    { id: 'application', label: t('nav.application'), icon: 'orders', rows: links(['/application']) },
    { id: 'account', label: t('runnerNav.account'), icon: 'account', rows: [...links(['/photo', '/banking']), { label: t('nav.account'), to: '/account' }, { label: es ? 'Eliminar cuenta' : 'Delete Account', to: '/account/delete' }] },
  ] : [
    { id: 'orders', label: t('runnerNav.orders'), icon: 'orders', rows: [
      { label: es ? 'Hoy' : 'Today', to: '/runs?view=today', badge: counts.today },
      { label: es ? 'Próximas rutas' : 'Upcoming', to: '/runs?view=upcoming', badge: counts.upcoming },
      { label: es ? 'Pendiente de aprobación' : 'Pending Approval', to: '/pending-approval', badge: counts.pending },
      { label: es ? 'Disputas' : 'Disputes', to: '/disputes', badge: counts.disputes },
      ...links(['/open-runs']),
    ] },
    { id: 'customers', label: t('nav.customers'), icon: 'customers', rows: links(['/customers', '/potential-customers']).map(link => ({ ...link, ...(link.to === '/potential-customers' ? { badge: counts.potential } : {}) })) },
    { id: 'account', label: t('runnerNav.account'), icon: 'account', rows: [
      { label: t('runnerNav.profile'), to: '/account' },
      ...links(['/photo']), { label: t('nav.insurance'), to: '/insurance' },
      ...links(['/banking', '/earnings', '/schedule', '/record']),
      { label: es ? 'Privacidad y notificaciones' : 'Privacy & Notifications', to: '/privacy' },
      { label: es ? 'Eliminar cuenta' : 'Delete Account', to: '/account/delete' },
    ] },
  ];
  const help = { id: 'help', label: t('runnerNav.help'), icon: 'help', rows: [
    { label: t('runnerNav.faq'), to: `${publicSite}/faq?topic=drive`, external: true },
    { label: t('runnerNav.contact'), to: `${publicSite}/contact?topic=drive`, external: true },
    { label: t('header.kit'), to: '/kit' }, { label: t('header.handbook'), to: '/handbook' },
  ] };
  const icon = (name: string) => <Box component="img" src={`/brand/navigation/${name}.svg`} alt="" sx={{ width: 24, height: 24, flexShrink: 0 }} />;
  const row = (item: Row, indent = false) => {
    const [path, query] = item.to.split('?');
    const active = !item.external && (query ? pathname === path && new URLSearchParams(search).get('view') === new URLSearchParams(query).get('view') : path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`));
    return <Box key={item.to} component={item.external ? 'a' : Link} {...(item.external ? { href: item.to } : { to: item.to })}
      aria-current={active ? 'page' : undefined} onClick={onNavigate}
      sx={{ ...rowSx, pl: indent ? '44px' : '18px', position: 'relative', ...(active ? { bgcolor: theme.selected, color: theme.green, textDecoration: 'underline', '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, bgcolor: theme.rail } } : {}) }}>
      {item.icon && icon(item.icon)}<Box component="span" sx={{ flex: 1, minWidth: 0 }}>{item.label}</Box>
      {item.badge && <Count {...item.badge} />}
    </Box>;
  };
  const group = (g: { id: string; label: string; icon: string; rows: Row[] }) => <Box key={g.id}>
    <Box component="button" type="button" aria-expanded={!closed[g.id]} aria-controls={`runner-nav-${g.id}`} onClick={() => setClosed(c => ({ ...c, [g.id]: !c[g.id] }))}
      sx={{ ...rowSx, border: 0, cursor: 'pointer', bgcolor: theme.group, color: theme.navy, fontWeight: 600 }}>
      {icon(g.icon)}<Box component="span" sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>{g.label}</Box>
      <Count {...sectionCount(g.rows.flatMap(r => r.badge ? [r.badge] : []))} />
      <Box sx={{ display: 'flex', transform: closed[g.id] ? 'rotate(180deg)' : undefined }}>{icon('up')}</Box>
    </Box>
    <Box id={`runner-nav-${g.id}`} hidden={Boolean(closed[g.id])}>{g.rows.map(r => row(r, true))}</Box>
  </Box>;
  const logout = async () => {
    setSigningOut(true); setSignOutError(false);
    try { await signOut(); window.location.assign('/login'); }
    catch { setSignOutError(true); setSigningOut(false); }
  };
  return <Box component="nav" aria-label={t('header.primaryNav')} sx={{ py: 2, bgcolor: theme.white }}>
    {!isApplicant && row({ label: t('runnerNav.home'), to: '/', icon: 'home' })}
    {groups.slice(0, 2).map(group)}
    {showInbox && row({ label: t('runnerNav.messages'), to: '/messages', icon: 'mail', badge: { count: unreadTotal, attention: true } })}
    {showInbox && row({ label: es ? 'Historial de pedidos' : 'Order History', to: '/history', icon: 'history' })}
    {groups.slice(2).map(group)}
    {group(help)}
    <Box component="button" type="button" onClick={() => void i18n.changeLanguage(i18n.language.startsWith('es') ? 'en' : 'es')} sx={{ ...rowSx, border: 0, bgcolor: theme.white, cursor: 'pointer' }}>{icon('language')}English / Español</Box>
    <Box component="button" type="button" disabled={signingOut} onClick={() => void logout()} sx={{ ...rowSx, border: 0, bgcolor: theme.white, cursor: 'pointer' }}>{icon('logout')}{t('header.signOut')}</Box>
    {signOutError && <Typography role="alert" sx={{ px: 2, color: '#B91C1C', fontSize: '14px' }}>{t('runnerNav.signOutFailed')}</Typography>}
  </Box>;
}
